"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "../i18n";

import BillingIcon from "../assets/icons/billing-icon.png";
import IdentityIcon from "../assets/icons/identity-icon.png";
import MapIcon from "../assets/icons/map-icon.png";
import BuddyIcon from "../assets/icons/buddy-icon.png";
import WebAppIcon from "../assets/icons/webapp-icon.png";
import IntegratedBookablesIcon from "../assets/icons/integrated-bookables-icon.png";

export default function AppFeaturesShowcase() {
  const { t } = useTranslation();
  const [activeFeature, setActiveFeature] = useState("billing");

  const features = [
    {
      id: "billing",
      title: t("routes.webApp.components.showCase.features.billing.title"),
      subtitle: t("routes.webApp.components.showCase.features.billing.subtitle"),
      description: t(
        "routes.webApp.components.showCase.features.billing.description"
      ),
      icon: BillingIcon,
      color: "from-green-500 to-purple-600",
      bullets: [
        t("routes.webApp.components.showCase.features.billing.bullets.0"),
        t("routes.webApp.components.showCase.features.billing.bullets.1"),
        t("routes.webApp.components.showCase.features.billing.bullets.2"),
      ],
    },
    {
      id: "identity",
      title: t("routes.webApp.components.showCase.features.identity.title"),
      subtitle: t("routes.webApp.components.showCase.features.identity.subtitle"),
      description: t(
        "routes.webApp.components.showCase.features.identity.description"
      ),
      icon: IdentityIcon,
      color: "from-cyan-500 to-blue-600",
      bullets: [
        t("routes.webApp.components.showCase.features.identity.bullets.0"),
        t("routes.webApp.components.showCase.features.identity.bullets.1"),
        t("routes.webApp.components.showCase.features.identity.bullets.2"),
      ],
    },
    {
      id: "map",
      title: t("routes.webApp.components.showCase.features.map.title"),
      subtitle: t("routes.webApp.components.showCase.features.map.subtitle"),
      description: t(
        "routes.webApp.components.showCase.features.map.description"
      ),
      icon: MapIcon,
      color: "from-purple-500 to-pink-600",
      bullets: [
        t("routes.webApp.components.showCase.features.map.bullets.0"),
        t("routes.webApp.components.showCase.features.map.bullets.1"),
        t("routes.webApp.components.showCase.features.map.bullets.2"),
      ],
    },
    {
      id: "buddy",
      title: t("routes.webApp.components.showCase.features.buddy.title"),
      subtitle: t("routes.webApp.components.showCase.features.buddy.subtitle"),
      description: t(
        "routes.webApp.components.showCase.features.buddy.description"
      ),
      icon: BuddyIcon,
      color: "from-purple-500 to-orange-500",
      bullets: [
        t("routes.webApp.components.showCase.features.buddy.bullets.0"),
        t("routes.webApp.components.showCase.features.buddy.bullets.1"),
        t("routes.webApp.components.showCase.features.buddy.bullets.2"),
      ],
    },
    {
      id: "integrated-bookables",
      title: t(
        "routes.webApp.components.showCase.features.integratedBookables.title"
      ),
      subtitle: t(
        "routes.webApp.components.showCase.features.integratedBookables.subtitle"
      ),
      description: t(
        "routes.webApp.components.showCase.features.integratedBookables.description"
      ),
      icon: IntegratedBookablesIcon,
      color: "from-blue-500 to-purple-600",
      bullets: [
        t(
          "routes.webApp.components.showCase.features.integratedBookables.bullets.0"
        ),
        t(
          "routes.webApp.components.showCase.features.integratedBookables.bullets.1"
        ),
        t(
          "routes.webApp.components.showCase.features.integratedBookables.bullets.2"
        ),
      ],
    },
  ];

  const activeFeatureData =
    features.find((f) => f.id === activeFeature) || features[0];

  return (
    <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-gray-100">
      <div className="text-center pt-8 pb-4"></div>

      <div className="border-b border-gray-200">
        <div className="flex overflow-x-auto scrollbar-hide">
          {features.map((feature) => (
            <button
              key={feature.id}
              className={`px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeFeature === feature.id
                  ? `border-b-2 border-${
                      feature.id === "billing"
                        ? "green"
                        : feature.id === "identity"
                        ? "cyan"
                        : feature.id === "map"
                        ? "purple"
                        : feature.id === "buddy"
                        ? "amber"
                        : feature.id === "webapp"
                        ? "pink"
                        : "blue"
                    }-600 text-gray-900`
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
              onClick={() => setActiveFeature(feature.id)}
            >
              <div className="flex items-center">
                <div className="w-8 h-8 mr-2">
                  <img
                    src={feature.icon}
                    alt={feature.title}
                    className="w-full h-full object-contain"
                  />
                </div>
                {feature.title}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeFeature}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-6">
              <div className="inline-flex items-center">
                <div className="w-6 h-6 mr-2">
                  <img
                    src={activeFeatureData.icon}
                    alt={activeFeatureData.title}
                    className="w-full h-full object-contain"
                  />
                </div>
                <span className="text-lg font-medium text-gray-900">
                  {activeFeatureData.title}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  {activeFeatureData.subtitle}
                </h3>
                <p className="text-gray-600 mb-6">
                  {activeFeatureData.description}
                </p>

                <div className="space-y-3 mb-6">
                  {activeFeatureData.bullets.map((bullet, index) => (
                    <div key={index} className="flex items-start">
                      <div
                        className={`flex-shrink-0 h-5 w-5 rounded-full bg-gradient-to-r ${activeFeatureData.color} flex items-center justify-center text-white mr-3`}
                      >
                        <svg
                          className="h-3 w-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <span className="text-gray-700">{bullet}</span>
                    </div>
                  ))}
                </div>

                <div className="flex space-x-4">
                  <a
                    href={`https://juhuu.app/${
                      activeFeature === "integrated-bookables"
                        ? "webapp"
                        : activeFeature
                    }`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`px-6 py-2 rounded-full bg-gradient-to-r ${activeFeatureData.color} text-white font-medium`}
                  >
                    {t('routes.webApp.components.showCase.learnMore')}
                  </a>
                  <a
                    href="#contact"
                    className="px-6 py-2 rounded-full border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  >
                    {t('routes.webApp.components.showCase.contactSales')}
                  </a>
                </div>
              </div>

              <div className="relative">
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${activeFeatureData.color} opacity-10 rounded-xl blur-xl`}
                ></div>
                <div className="relative rounded-xl overflow-hidden border border-gray-200 shadow-lg">
                  <div className="bg-white p-8 flex items-center justify-center h-[400px]">
                    <div className="w-32 h-32">
                      <img
                        src={activeFeatureData.icon}
                        alt={activeFeatureData.title}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
